import { initAnimalSettings } from '@/components/Colony/Colony.utils';
import { Animal, AnimalV1 } from '@/model/Animal.model';
import { ClassificationType, Treatment } from '@/model/Treatment.model';
import Http from '@/support/http';
import { api as apiRoute, web as webRoute } from '@/support/route';
import { StepFormState } from '@/components/UI/StepForm/StepForm.model';
import { ID } from '@/model/Common.model';
import { Study } from '@/model/Study.model';
import { animalMetadataColumns, studyCalculationColumns } from '@/components/Animals/AnimalsTable/AnimalsTable.utils';
import { calculateAge } from '@/helpers';
import { DateUtils } from '@/utils/Date.utils';
import Link from '@/components/UI/Link';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import { _notNil } from '@/littledash';
import { isDeadV1 } from '@/constants/utils';
import { AnimalMetadataFilter } from '@/components/Colony/Animals/Animals.utils';
import { PresetCalculation } from '@/model/PresetCalculation.model';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { FeatureFlagType } from '@/model/FeatureFlag.model.ts';

interface BulkDosage {
  animal_id: string;
  comment: string;
  dosed_at: string;
  treatment_id: string;
}

interface DiseaseInduction {
  induct: boolean;
}

export interface CreateBulkDosingState extends StepFormState<CreateBulkDosingState> {
  stepReady: boolean;
  finalStepReady: boolean;
  selectedAnimals: Animal[];
  selectedAnimals2: boolean;
  selectedTreatments: Treatment[];
  dosages: BulkDosage[];
  testBoolean: boolean;
  testNumber: number;
  testAlpha: string;
  study: Study;
  animals: any;
  treatmentClassification: ClassificationType;
  inductDisease: DiseaseInduction;
}

export const setupInitialState = async (
  studyId: ID,
  treatmentClassification: ClassificationType,
  steps: any[]
): Promise<CreateBulkDosingState> => {
  const url = apiRoute('studies.show.p', { id: studyId }, { include: 'study_groups' });
  const data = await Http.get(url);
  const study = data.data.data;

  const {
    data: { data: metadataData },
  } = await Http.get(apiRoute('meta-glossary.show'), {
    params: {
      filter_type: 'subject_meta',
    },
  });

  const initialAnimalSettings = initAnimalSettings([], true, false).settings;

  const initialState = {
    selectedAnimals: [],
    selectedAnimals2: false,
    testBoolean: false,
    testNumber: 123,
    testAlpha: 'abc',
    selectedTreatments: [],
    dosages: [],
    treatmentClassification: treatmentClassification,
    inductDisease: { induct: true },
    stepReady: false,
    finalStepReady: false,
    currentStep: 0,
    steps,
    complete: false,
    study,

    animals: {
      species: undefined,
      initialData: [],
      data: [],
      settings: initialAnimalSettings,
      cellErrors: {},
      metadata: metadataData,
      metaGlossaries: [],
      invalidCells: {},
      overlay: '',
    },
    Summary: {},
    apiError: undefined,
  };

  return initialState;
};

export const dosingAnimalTableColumns = (
  studyId: ID,
  calculations: Array<PresetCalculation>,
  columns: Record<string, boolean>,
  changePercentage: string,
  features: Partial<Record<FeatureFlagType, boolean>>,
  metadataOptions?: Array<AnimalMetadataFilter>
): Array<TanStackColumnDef<AnimalV1>> => {
  const calculationColumns = studyCalculationColumns(calculations, columns, changePercentage);
  const metadataColumns = animalMetadataColumns(metadataOptions, columns);

  return [
    {
      id: 'number',
      header: 'No.',
      size: 75,
      accessorKey: 'number',
      isVisible: columns['number'],
      enableSorting: true,
    },
    {
      id: 'catalog',
      header: 'Animal No.',
      accessorKey: 'catalog',
      isVisible: columns['catalog'],
      enableSorting: true,
    },
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      isVisible: columns['id'],
      enableSorting: true,
      cell: ({
        row: {
          original: { id: animalId, name },
        },
      }: {
        row: { original: AnimalV1 };
      }) => (
        <div className="flex justify-between items-center w-100">
          <Link
            to={webRoute('animals.show', {
              id: studyId,
              subject_id: animalId,
            })}
            className="link blue"
          >
            {name}
          </Link>
        </div>
      ),
    },
    {
      id: 'tail',
      header: 'Tail ID',
      accessorFn: (row: AnimalV1) => row.alt_ids?.tail,
      isVisible: columns['tail'],
      enableSorting: false,
    },
    {
      id: 'donor',
      header: 'Donor ID',
      accessorFn: (row: AnimalV1) => row.alt_ids?.donor,
      isVisible: columns['donor'],
      enableSorting: false,
    },
    {
      id: 'ear',
      header: 'Ear ID',
      accessorFn: (row: AnimalV1) => row.alt_ids?.ear,
      isVisible: columns['ear'],
      enableSorting: false,
    },
    {
      id: 'tag',
      header: 'Tag ID',
      accessorFn: (row: AnimalV1) => row.alt_ids?.tag,
      isVisible: columns['tag'],
      enableSorting: false,
    },
    {
      id: 'cage',
      header: 'Cage',
      accessorFn: (row: AnimalV1) => row.cage?.name,
      isVisible: columns['cage'],
      enableSorting: true,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row: { original } }: { row: { original: AnimalV1 } }) => isDeadV1(original) || '-',
    },
    ...(calculationColumns as any),
    {
      id: 'study_group',
      header: 'Study group',
      accessorFn: (row: AnimalV1) => row.study_group?.no,
      isVisible: columns['study_group'],
      size: 200,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: AnimalV1 } }) => {
        const studyGroup = original.study_group;
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return studyGroup ? <GroupLabel group={studyGroup} /> : '-';
      },
    },
    {
      id: 'tracking_started_at',
      header: 'Tracking date',
      accessorKey: 'tracking_started_at',
      isVisible: columns['tracking_started_at'],
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: AnimalV1 } }) => {
        return <>{DateUtils.renderDate(original.tracking_started_at, { defaultResponse: '' })}</>;
      },
    },
    {
      id: 'disease_inducted_at',
      header: 'Disease Induction Date',
      accessorKey: 'disease_inducted_at',
      isVisible: columns['disease_inducted_at'],
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: AnimalV1 } }) => {
        return <>{DateUtils.renderDate(original.disease_inducted_at, { defaultResponse: '' })}</>;
      },
    },
    {
      id: 'sex',
      header: 'Sex',
      accessorKey: 'sex',
      isVisible: columns['sex'],
      size: 75,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: AnimalV1 } }) => original.sex?.toUpperCase?.() ?? '-',
    },
    {
      id: 'dob',
      header: 'DOB',
      accessorKey: 'dob',
      isVisible: columns['dob'],
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: AnimalV1 } }) => {
        return <>{DateUtils.renderDate(original.dob, { defaultResponse: '-' })}</>;
      },
    },
    {
      id: 'age',
      header: 'Age',
      accessorKey: 'ageValue',
      isVisible: columns['age'],
      enableSorting: false,
      cell: ({ row: { original } }: { row: { original: AnimalV1 } }) => {
        return _notNil(original.dob) ? calculateAge(original.dob, original.terminated_at) : '-';
      },
    },
    {
      id: 'species',
      header: 'Species',
      accessorKey: 'species_name',
      isVisible: columns.species ?? false,
      enableSorting: false,
    },
    {
      id: 'strain',
      header: 'Strain',
      accessorKey: 'strain_name',
      isVisible: columns.strain ?? false,
      enableSorting: false,
    },
    ...(metadataColumns as any),
  ];
};
